import { NewFamiliarCard } from "@/components/patient/FamiliarAlbum";
import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Home, RotateCcw, ClipboardList, Volume2 } from "lucide-react";
import { getGardien } from "@/data/gardiens";
import { speakDevice, stopDevice } from "@/lib/deviceVoice";
import { cachedBravo } from "@/lib/offline";

const spokenText = (m, g) => `Bravo ! ${m?.lines ? m.lines.map((l) => l.text).join(" ") : m?.text || g.quote}`;

export default function SuccessScreen({ score, onHome, onAgain, onNote, gardienId = "leontine", newFamiliar }) {
  const g = getGardien(gardienId);
  const [msg, setMsg] = useState(null);
  const audio = useRef(null);
  useEffect(() => {
    let alive = true;
    api.get(`/gardiens/${g.id}/congrats`).catch((e) => {
      // Sans internet : félicitations gardées sur la tablette.
      const kept = !e.response && cachedBravo(g.id);
      if (kept) return { data: kept };
      throw e;
    }).then(({ data }) => {
      if (!alive) return;
      setMsg(data);
      const voice = data.bravo_url || data.audio_url;
      if (voice) {
        const a = new Audio(`${process.env.REACT_APP_BACKEND_URL}${voice}`);
        audio.current = a;
        a.play().catch(() => {});
      } else speakDevice(spokenText(data, g));
    }).catch(() => speakDevice(spokenText(null, g)));
    return () => { alive = false; audio.current?.pause(); stopDevice(); };
  }, [g.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const replay = () => { const a = audio.current; if (a) { a.currentTime = 0; a.play().catch(() => {}); } else speakDevice(spokenText(msg, g)); };
  return (
    <div
      className="min-h-screen bg-[#FAF7F2] flex flex-col items-center justify-center p-8 text-center soft-in grain"
      data-testid="success-screen"
    >
      <div className="relative mb-6">
        <div className="w-56 h-56 rounded-full overflow-hidden border-8 gardien-alive" style={{ borderColor: `${g.color}55` }}>
          <img src={g.image} alt={g.name} className="w-full h-full object-cover object-top" data-testid="success-gardien-image" />
        </div>
        {[["-top-2 left-4", "0s"], ["top-6 -right-4", "0.9s"], ["bottom-10 -left-5", "1.7s"]].map(([pos, d]) => (
          <span key={pos} className={`alive-sparkle ${pos}`} style={{ animationDelay: d }} aria-hidden="true" />
        ))}
        {g.familiar && <img src={g.familiar.image} alt={g.familiar.name} title={`${g.familiar.name}, ${g.familiar.title}`} data-testid="success-familiar-image"
          className="absolute -bottom-2 -right-2 w-24 h-24 rounded-full object-cover border-4 border-white shadow-lg familiar-cheer" />}
      </div>
      <h1 className="text-5xl sm:text-6xl font-serif-display italic text-emerald-700 mb-4">Bravo !</h1>
      <p className="text-2xl font-patient text-gray-700 mb-2">Exercice terminé avec succès</p>
      <p className="text-xl font-patient text-gray-500 mb-6">Résultat : {score}%</p>
      <div className="font-serif-display italic text-xl sm:text-2xl text-gray-700 max-w-lg mb-4" data-testid="success-gardien-quote">
        {msg?.lines ? msg.lines.map((l) => (
          <p key={l.speaker} className="mb-2">« {l.text} » <span className="not-italic font-sans text-base text-gray-500">— {l.speaker}</span></p>
        )) : (
          <p>« {msg?.text || g.quote} » <span className="block text-base not-italic font-sans text-gray-500 mt-1">— {g.name}, {g.title}</span></p>
        )}
      </div>
      {msg ? (
        <button onClick={replay} data-testid="success-gardien-replay"
          className="mb-10 inline-flex items-center gap-2 h-12 px-5 rounded-full bg-white border-2 border-emerald-200 text-emerald-800 font-patient font-bold active:scale-95 transition-transform">
          <Volume2 className="w-5 h-5" /> Réécouter {g.name}
        </button>
      ) : <div className="mb-8" />}
      {newFamiliar && <NewFamiliarCard id={newFamiliar} />}

      <div className="flex flex-col sm:flex-row gap-6 w-full max-w-lg">
        <button
          onClick={onAgain}
          data-testid="success-again-button"
          className="flex-1 h-20 rounded-full bg-amber-200 text-gray-800 font-patient font-bold text-2xl flex items-center justify-center gap-3 active:scale-95 transition-transform duration-200 border-4 border-amber-300"
        >
          <RotateCcw className="w-8 h-8" /> Rejouer
        </button>
        <button
          onClick={onHome}
          data-testid="success-home-button"
          className="flex-1 h-20 rounded-full bg-emerald-700 text-white font-patient font-bold text-2xl flex items-center justify-center gap-3 active:scale-95 transition-transform duration-200"
        >
          <Home className="w-8 h-8" /> Accueil
        </button>
      </div>

      {onNote && (
        <button
          onClick={onNote}
          data-testid="success-note-button"
          className="mt-6 inline-flex items-center gap-2 px-6 h-14 rounded-full bg-white border-4 border-emerald-700/20 text-emerald-800 font-patient font-bold text-lg active:scale-95 transition-transform duration-200"
        >
          <ClipboardList className="w-6 h-6" /> Note de suivi (soignant)
        </button>
      )}
    </div>
  );
}
